/**
 * AIF-03 — Import AI Findings wizard (`/ai-findings-import`, §11.5 / WF-C).
 *
 * Four steps: (1) pick DA → (2) model + version + files → (3) analyze overview
 * with confidence/scope tuning → (4) execute (async job poll + cancel + inline
 * ImportReport).
 *
 * Role-gated to expert_analyst/tenant_admin/platform_admin via
 * `useImportPermissions` (server is the security boundary; this just keeps an
 * ineligible user out of the flow). The page chrome is a `PageHeading` + a MUI
 * Stepper; it is NOT a list page so it doesn't use DataPageShell.
 *
 * Analyze re-runs whenever the floor or scope changes (debounced in step 3,
 * then mirrored here) so the would-counts + kept count stay live. Step 4 starts
 * the job with EXACTLY the floor/scope the overview previewed.
 */
import { useCallback, useRef, useState, type ReactElement } from "react";
import { Box, Button, Step, StepLabel, Stepper } from "@mui/material";
import { Navigate } from "react-router-dom";
import { PageHeading } from "@aeriosiq/ui";
import type {
  AnalyzeAiFindingsImportReport,
  DataAcquisition,
} from "@aeriosiq/types";
import { useImportPermissions } from "./useImportPermissions.js";
import {
  useAnalyzeImportMutation,
  useCancelImportMutation,
  useImportJobStatus,
  useStartImportMutation,
} from "./importQueries.js";
import { Step1PickDa } from "./Step1PickDa.js";
import { Step2ModelFiles, type Step2Selection } from "./Step2ModelFiles.js";
import { Step3Analyze, type Step3Tuning } from "./Step3Analyze.js";
import { Step4Execute } from "./Step4Execute.js";
import { percentToFraction } from "./confidence.js";

const STEPS = [
  "Data acquisition",
  "Model & files",
  "Analyze",
  "Execute",
] as const;

const DEFAULT_TUNING: Step3Tuning = {
  confidencePercent: 50,
  deletionScope: "ADDITIVE",
};

function errMsg(e: unknown): string {
  if (e && typeof e === "object" && "message" in e) {
    return String(e.message);
  }
  return "Something went wrong.";
}

export function ImportAiFindingsPage(): ReactElement {
  const { canImport } = useImportPermissions();

  const [activeStep, setActiveStep] = useState(0);
  const [da, setDa] = useState<DataAcquisition | null>(null);
  const [selection, setSelection] = useState<Step2Selection>({
    modelId: "",
    versionNumber: "latest",
    files: [],
  });
  const [tuning, setTuning] = useState<Step3Tuning>(DEFAULT_TUNING);
  const [report, setReport] = useState<AnalyzeAiFindingsImportReport | null>(
    null,
  );
  const [importId, setImportId] = useState<string | null>(null);

  const daId = da?.id ?? "";
  const analyzeMutation = useAnalyzeImportMutation(daId);
  const startMutation = useStartImportMutation(daId);
  const cancelMutation = useCancelImportMutation(daId);
  const jobStatus = useImportJobStatus(daId, importId);

  // Keep the latest analyze response in local state (mutation result is
  // ephemeral across re-renders triggered by debounced re-runs).
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runAnalyze = useCallback(
    (t: Step3Tuning) => {
      if (!da || !selection.modelId || selection.files.length === 0) return;
      analyzeMutation.mutate(
        {
          files: selection.files,
          modelId: selection.modelId,
          versionNumber: selection.versionNumber,
          confidenceFloor: percentToFraction(t.confidencePercent),
          deletionScope: t.deletionScope,
        },
        {
          onSuccess: (data) => {
            setReport(data);
          },
        },
      );
    },
    // analyzeMutation identity is stable enough; exclude to avoid re-run churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [da, selection],
  );

  // Re-run analyze (debounced) when tuning changes while on step 3.
  function handleTuningChange(next: Step3Tuning): void {
    setTuning(next);
    if (activeStep !== 2) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runAnalyze(next);
    }, 300);
  }

  // Capture the terminal job report into the wizard so the poll can stop.
  const job = jobStatus.data ?? null;

  if (!canImport) {
    return <Navigate to="/" replace />;
  }

  function goNext(): void {
    if (activeStep === 1) {
      // Entering step 3 → fire the initial analyze.
      setActiveStep(2);
      setReport(null);
      runAnalyze(tuning);
      return;
    }
    if (activeStep === 2) {
      // Entering step 4 → start the import job.
      setActiveStep(3);
      startMutation.mutate(
        {
          files: selection.files,
          modelId: selection.modelId,
          versionNumber: selection.versionNumber,
          confidenceFloor: percentToFraction(tuning.confidencePercent),
          deletionScope: tuning.deletionScope,
        },
        {
          onSuccess: (data) => {
            setImportId(data.importId);
          },
        },
      );
      return;
    }
    setActiveStep((s) => s + 1);
  }

  function goBack(): void {
    setActiveStep((s) => Math.max(0, s - 1));
  }

  function handleCancel(): void {
    if (importId) cancelMutation.mutate(importId);
  }

  const canAdvance = ((): boolean => {
    if (activeStep === 0) return da !== null;
    if (activeStep === 1)
      return selection.modelId !== "" && selection.files.length > 0;
    if (activeStep === 2) return report !== null && !analyzeMutation.isPending;
    return false;
  })();

  const nextLabel =
    activeStep === 1 ? "Analyze" : activeStep === 2 ? "Confirm import" : "Next";

  return (
    // R0627-12 — drop the `p: 1` inset so the heading sits on the same top
    // baseline as the WindPlatform wordmark (matching the DataPageShell-based
    // AI Models page). R0627-09 — `height: 100%` + a `flex: 1` body lets the
    // step-1 table grow into the whole viewport instead of leaving dead space.
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 3,
        minHeight: 0,
      }}
    >
      <PageHeading>Import AI Findings</PageHeading>

      <Stepper activeStep={activeStep} sx={{ maxWidth: 720, flexShrink: 0 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box
        sx={{
          flex: 1,
          minHeight: 320,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {activeStep === 0 && (
          <Step1PickDa
            selectedId={da?.id ?? null}
            onSelect={(picked) => {
              setDa(picked);
            }}
          />
        )}
        {activeStep === 1 && (
          <Step2ModelFiles value={selection} onChange={setSelection} />
        )}
        {activeStep === 2 && (
          <Step3Analyze
            report={report}
            isAnalyzing={analyzeMutation.isPending}
            error={
              analyzeMutation.isError ? errMsg(analyzeMutation.error) : null
            }
            tuning={tuning}
            onTuningChange={handleTuningChange}
            // ANA-R1-15 — hand the in-memory label files so the per-class count
            // column recomputes live with the slider (no re-analyze round-trip).
            files={selection.files}
          />
        )}
        {activeStep === 3 && (
          <Step4Execute
            job={job}
            starting={startMutation.isPending || (importId !== null && !job)}
            startError={
              startMutation.isError ? errMsg(startMutation.error) : null
            }
            onCancel={handleCancel}
            cancelling={cancelMutation.isPending}
          />
        )}
      </Box>

      {activeStep < 3 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            maxWidth: 960,
            flexShrink: 0,
          }}
        >
          <Button onClick={goBack} disabled={activeStep === 0}>
            Back
          </Button>
          <Button variant="contained" onClick={goNext} disabled={!canAdvance}>
            {nextLabel}
          </Button>
        </Box>
      )}
    </Box>
  );
}
